import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Cookie } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import {
  COOKIE_CONSENT_KEY,
  parseConsent,
  buildConsentChoice,
  serializeConsent,
  type ConsentData,
} from "@/hooks/use-cookie-consent";

export default function CookieConsent() {
  const [visible, setVisible] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(COOKIE_CONSENT_KEY);
      const saved = parseConsent(raw);
      if (!saved) {
        setVisible(true);
      } else {
        setAnalytics(saved.analytics);
        setMarketing(saved.marketing);
      }
    } catch {
      setVisible(true);
    }
  }, []);

  const save = (data: Partial<ConsentData> & { choice?: ConsentData["choice"] }) => {
    const choice = data.choice ?? buildConsentChoice(data.analytics ?? analytics, data.marketing ?? marketing);
    const consent: ConsentData = {
      choice,
      analytics: data.analytics ?? (choice === "all" ? true : analytics),
      marketing: data.marketing ?? (choice === "all" ? true : marketing),
      ts: new Date().toISOString(),
    };

    try {
      localStorage.setItem(COOKIE_CONSENT_KEY, serializeConsent(consent));
    } catch {
      /* ignore */
    }
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      role="dialog"
      aria-live="polite"
      aria-label="Zgoda na pliki cookies"
      className="fixed inset-x-0 bottom-0 z-[100] border-t border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80 shadow-lg"
    >
      <div className="container mx-auto px-4 py-4">
        {!showSettings ? (
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div className="flex gap-3 items-start md:items-center">
              <Cookie className="h-5 w-5 text-primary shrink-0 mt-0.5 md:mt-0" />
              <p className="text-sm text-foreground/90">
                Używamy plików cookies, aby zapewnić działanie portalu i analizować ruch.
                Szczegóły w{" "}
                <Link to="/polityka-prywatnosci" className="underline text-primary">
                  Polityce Prywatności
                </Link>
                .
              </p>
            </div>
            <div className="flex flex-wrap gap-2 md:shrink-0">
              <Button variant="ghost" size="sm" onClick={() => setShowSettings(true)}>
                Ustawienia
              </Button>
              <Button variant="outline" size="sm" onClick={() => save({ analytics: false, marketing: false })}>
                Tylko niezbędne
              </Button>
              <Button size="sm" onClick={() => save({ analytics: true, marketing: true })}>
                Akceptuję wszystkie
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            <div className="flex items-center gap-2">
              <Cookie className="h-5 w-5 text-primary" />
              <h3 className="text-sm font-semibold">Ustawienia plików cookies</h3>
            </div>
            <div className="space-y-3">
              <div className="flex items-start justify-between gap-4 rounded-md border border-border p-3">
                <div>
                  <p className="text-sm font-medium">Niezbędne</p>
                  <p className="text-xs text-muted-foreground">
                    Wymagane do działania portalu (logowanie, sesja). Nie można wyłączyć.
                  </p>
                </div>
                <Switch checked disabled />
              </div>
              <div className="flex items-start justify-between gap-4 rounded-md border border-border p-3">
                <div>
                  <p className="text-sm font-medium">Analityczne</p>
                  <p className="text-xs text-muted-foreground">
                    Pomagają nam zrozumieć, jak korzystasz z portalu (np. Vercel Analytics).
                  </p>
                </div>
                <Switch checked={analytics} onCheckedChange={setAnalytics} />
              </div>
              <div className="flex items-start justify-between gap-4 rounded-md border border-border p-3">
                <div>
                  <p className="text-sm font-medium">Marketingowe</p>
                  <p className="text-xs text-muted-foreground">
                    Umożliwiają personalizację reklam i pomiar ich skuteczności.
                  </p>
                </div>
                <Switch checked={marketing} onCheckedChange={setMarketing} />
              </div>
            </div>
            <div className="flex flex-wrap gap-2 justify-end">
              <Button variant="ghost" size="sm" onClick={() => setShowSettings(false)}>
                Wróć
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => save({ analytics, marketing })}
              >
                Zapisz wybór
              </Button>
              <Button size="sm" onClick={() => save({ analytics: true, marketing: true })}>
                Akceptuję wszystkie
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}